import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './pages/general/login/login.component';
import { ScormComponent } from './pages/general/scorm/scorm.component';
import { HomeComponent } from './pages/learner/home/home.component';
import { CallComponent } from './pages/learner/call/call.component';
import { ResultsConversationalComponentNew } from './pages/learner/results/results-conversational-new/results-conversational-new.component';
import { ResultsGuidedComponent } from './pages/learner/results/results-guided/results-guided.component';
import { TeamAdminHomeComponent } from './pages/team-admin/team-admin-home/team-admin-home.component';
import { authGuard } from './guard/auth.guard';
import { LayoutGeneralComponent } from './layouts/layout-general/layout-general.component';
import { MyTeamsComponent } from './pages/team-admin/my-teams/my-teams.component';
import { LearnerDashboardComponent } from './pages/learner/learner-dashboard/learner-dashboard.component';
import { LoginCallbackComponent } from './pages/general/login-callback/login-callback.component';
import { roleGuard } from './guard/role.guard';
import { nativeIosGuard } from './guard/native-ios.guard';
import { AssignmentComponent } from './pages/learner/assignment/assignment.component';
import { LearningPathwayComponent } from './pages/learner/learning-pathway/learning-pathway.component';
import { SimulationBuilderComponent } from './pages/self-serve/simulation-builder/simulation-builder.component';
import { SimulationDetailsComponent } from './pages/self-serve/simulation-builder/simulation-details/simulation-details.component';
import { ResultsVisualAidComponent } from './pages/learner/results/results-visual-aid/results-visual-aid.component';
import { provideTranslocoScope } from '@jsverse/transloco';
import { TranslocoResolver } from 'transloco-resolver';
import { DashboardComponent } from './pages/self-serve/simulation-builder/dashboard/dashboard.component';
import { AssignmentDetailsComponent } from './pages/self-serve/simulation-builder/assignment-details/assignment-details.component';
import { UserDetailPageComponent } from './pages/self-serve/simulation-builder/user-details/user-detail-page.component';
import { ReportingDashboardComponent } from './pages/reporting-dashboard/reporting-dashboard.component';
import { PracticeDetailComponent } from './pages/reporting-dashboard/dashboard-tabs/assignments-tab/practice-tab/practice-detail/practice-detail.component';
import { CertificationDetailComponent } from './pages/reporting-dashboard/dashboard-tabs/assignments-tab/certification-tab/certification-detail/certification-detail.component';
import { UserDetailsComponent } from './pages/reporting-dashboard/dashboard-tabs/users-tab/user-details/user-details.component';
import { LeaderboardComponent } from './pages/leaderboard/leaderboard.component';
import { AssessmentPreCallComponent } from './pages/learner/call/call-pathway-assessment/assessment-pre-call/assessment-pre-call.component';
import { AssessmentCallPageComponent } from './pages/learner/call/call-pathway-assessment/assessment-call-page/assessment-call-page.component';
import { PersonalizedPracticeComponent } from './pages/personalized-practice/personalized-practice.component';
import { PersonalizedProductDetailsComponent } from './pages/personalized-practice/personalized-product-details/personalized-product-details.component';
import { PersonalizedPracticeDetailsComponent } from './pages/reporting-dashboard/dashboard-tabs/personalized-practice-tab/personalized-practice-details/personalized-practice-details.component';
import { SimulationDetailComponent } from './pages/reporting-dashboard/dashboard-tabs/assignments-tab/simulation-detail/simulation-detail.component';
import { IosAccountComponent } from './pages/learner/ios-account/ios-account.component';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [authGuard],
    providers: [provideTranslocoScope('login')],
    resolve: { translations: TranslocoResolver },
  },
  {
    path: 'login-callback',
    component: LoginCallbackComponent,
    canActivate: [authGuard],
  },
  {
    path: 'logout',
    component: LoginComponent,
    canActivate: [authGuard],
    providers: [provideTranslocoScope('login')],
    resolve: { translations: TranslocoResolver },
  },
  {
    path: 'scorm',
    component: ScormComponent,
  },
  {
    path: '',
    component: LayoutGeneralComponent,
    canActivate: [authGuard],
    providers: [provideTranslocoScope('general')],
    resolve: { translations: TranslocoResolver },
    children: [
      { path: '', component: HomeComponent, canActivate: [roleGuard] },
      { path: 'home', component: HomeComponent, canActivate: [roleGuard] },
      // Legacy Reporting V1 entry points: roleGuard redirects them to the reporting
      // dashboard when accessible, otherwise to assignments.
      { path: 'reporting', component: HomeComponent, canActivate: [roleGuard] },
      { path: 'reporting_visualization', component: HomeComponent, canActivate: [roleGuard] },
      {
        path: 'call',
        component: CallComponent,
        providers: [
          provideTranslocoScope('call'),
          provideTranslocoScope('settings'),
        ],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'results_conversational',
        component: ResultsConversationalComponentNew,
        providers: [provideTranslocoScope('results')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'result_personalized_practice',
        component: ResultsConversationalComponentNew,
        providers: [provideTranslocoScope('results')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'results_guided',
        component: ResultsGuidedComponent,
        providers: [provideTranslocoScope('results')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'assignments',
        component: LearnerDashboardComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('assignment')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'assignment',
        component: AssignmentComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('assignment')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'learning-pathway',
        component: LearningPathwayComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('assignment')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'assessment-pre-call',
        component: AssessmentPreCallComponent,
        canActivate: [roleGuard],
      },
      {
        path: 'assessment-call',
        component: AssessmentCallPageComponent,
        canActivate: [roleGuard],
      },
      {
        path: 'results_visual_aid',
        component: ResultsVisualAidComponent,
        providers: [provideTranslocoScope('results')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'reporting_dashboard',
        component: ReportingDashboardComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reporting-dashboard')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'reporting_dashboard/practice/:id',
        component: PracticeDetailComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reporting-dashboard')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'reporting_dashboard/certification/:id',
        component: CertificationDetailComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reporting-dashboard')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'reporting_dashboard/simulation/:id',
        component: SimulationDetailComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reportingV2')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'reporting_dashboard/user/:id',
        component: UserDetailsComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reporting-dashboard')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'reporting_dashboard/personalized-practice/:id',
        component: PersonalizedPracticeDetailsComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('reporting-dashboard')],
        resolve: { translations: TranslocoResolver }
      },
      {
        path: 'personalized-practice',
        component: PersonalizedPracticeComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('personalizedPractice')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'personalized-practice/details/:uuid',
        component: PersonalizedProductDetailsComponent,
        canActivate: [roleGuard, nativeIosGuard],
        providers: [provideTranslocoScope('personalizedPractice')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'self-serve',
        component: SimulationBuilderComponent,
        canActivate: [roleGuard, nativeIosGuard],
        children: [
          {
            path: '',
            component: DashboardComponent,
          },
          {
            path: 'simulations/:uuid',
            component: SimulationDetailsComponent,
          },
          {
            path: 'assignments/:uuid',
            component: AssignmentDetailsComponent,
          },
          {
            path: 'users/:uuid',
            component: UserDetailPageComponent,
          },
        ],
      },

      {
        path: 'leaderboard',
        component: LeaderboardComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('leaderboard')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'ios-account',
        component: IosAccountComponent,
      },
    ],
  },
  {
    path: 'team_admin/my_teams',
    redirectTo: 'team_manager/my_teams',
    pathMatch: 'full',
  },
  {
    path: 'team_manager',
    component: LayoutGeneralComponent,
    canActivate: [authGuard, nativeIosGuard],
    providers: [provideTranslocoScope('teams')],
    resolve: { translations: TranslocoResolver },
    children: [
      {
        path: '',
        component: TeamAdminHomeComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('teams')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'home',
        component: TeamAdminHomeComponent,
        canActivate: [roleGuard],
      },
      {
        path: 'my_teams',
        component: MyTeamsComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('teams')],
        resolve: { translations: TranslocoResolver },
      },
      {
        path: 'assignments',
        component: LearnerDashboardComponent,
        canActivate: [roleGuard],
        providers: [provideTranslocoScope('assignment')],
        resolve: { translations: TranslocoResolver },
      },
    ],
  },
];
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
